import {
    createContext, ReactNode, useContext, useMemo, useState,
} from 'react';

import Parent from 'Components/Common/models/Parent';
import { UserRole } from 'api/dtos/user';
import { TeamMemberDto } from 'api/dtos/teamMember';
import { TagDto } from 'api/dtos/tag';

type TeamMemberDetails = {
    teamMemberId: string;
    name: string;
    email: string;
    phone?: string;
    address?: string;
    role: UserRole;
    appRole: UserRole;
    enabled: boolean;
    parents?: Parent[];
    tags: TagDto[];
};
interface TeamMemberDetailsProps {
    values: TeamMemberDetails;
    updateValues: (updated: Partial<TeamMemberDetails>) => void;
    resetValues: (newInitialState: TeamMemberDetails) => void;
  }

const defaultValues: TeamMemberDetails = {
    teamMemberId: '',
    name: '',
    email: '',
    phone: '',
    address: '',
    role: UserRole.TeamMember,
    appRole: UserRole.TeamMember,
    enabled: false,
    parents: [],
    tags: [],
};

const TeamMemberContext = createContext<TeamMemberDetailsProps | undefined>(undefined);

export const TeamMemberProvider: React.FC<{
    children: ReactNode;
    initialState?: TeamMemberDetails;
  }> = ({ children, initialState }) => {
      const mergedInitial: TeamMemberDetails = { ...defaultValues, ...initialState };
      const [values, setValues] = useState<TeamMemberDetails>(mergedInitial);

      const updateValues = (updated: Partial<TeamMemberDetails>) => {
          setValues((prev) => ({ ...prev, ...updated }));
      };

      const resetValues = (newInitialState: TeamMemberDetails) => {
          setValues(newInitialState);
      };

      const valuesToProvide = useMemo(() => ({ values, updateValues, resetValues }), [values]);

      return (
          <TeamMemberContext.Provider value={valuesToProvide}>
              {children}
          </TeamMemberContext.Provider>
      );
  };

export const useTeamMemberContext = () => {
    const context = useContext(TeamMemberContext);
    if (!context) {
        throw new Error('useTeamMemberContext must be used within an TeamMemberProvider');
    }
    return context;
};
